import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { NgOptimizedImage } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormBuilder,
  FormGroup,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { Checkbox } from 'src/app/_models/checkbox';
import { ErrorObject } from 'src/app/_models/error';
import { FormBase } from 'src/app/_models/form-base';
import { AuthenticationService } from 'src/app/_services/authentication.service';
import { ButtonComponent } from '../../_components/buttons/button/button.component';
import { CheckboxComponent } from '../../_components/selection-controls/checkbox/checkbox.component';
import { MatFormFieldComponent } from '../../_components/forms/mat-form-field/mat-form-field.component';
import { MatIcon } from '@angular/material/icon';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgOptimizedImage,
    ReactiveFormsModule,
    CheckboxComponent,
    RouterLink,
    ButtonComponent,
    MatFormFieldComponent,
    MatIcon,
  ],
})
export class LoginComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly formBuilder = inject(FormBuilder);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly authenticationService = inject(AuthenticationService);

  readonly emailField = new FormBase({
    key: 'email',
    label: 'Email',
    type: 'email',
    placeholder: 'Email',
    disabled: false,
    required: true,
    value: '',
    options: [],
    autocomplete: 'email',
  });

  readonly passwordField = new FormBase({
    key: 'password',
    label: 'Password',
    type: 'password',
    placeholder: 'Password',
    disabled: false,
    required: true,
    value: '',
    options: [],
    autocomplete: 'current-password',
  });

  readonly rememberMe = new Checkbox('rememberMe', 'Remember me', 'done');

  readonly loginForm = this.formBuilder.group({
    [this.emailField.key]: ['', [Validators.required, Validators.email]],
    [this.passwordField.key]: ['', Validators.required],
    [this.rememberMe.id]: [false, Validators.required],
  });

  readonly returnUrl = signal('/');
  readonly isProcessing = signal(false);
  readonly loginError = signal<ErrorObject | null>(null);
  readonly displayMaintenancePage = signal(false);
  readonly tooltipText = signal('Stay signed in on this device after closing your browser. Not recommended on shared or public computers.');

  ngOnInit(): void {
    this.returnUrl.set(this.route.snapshot.queryParams['returnUrl'] ?? '/');
    const navState = history.state as { error?: string };
    if (navState?.error) {
      this.loginError.set({ status: 0, message: navState.error, code: null });
    }
  }

  get f(): FormGroup['controls'] {
    return this.loginForm.controls;
  }

  onSubmit(): void {
    if (this.isProcessing()) {
      return;
    }
    this.loginError.set(null);
    this.isProcessing.set(true);
    sessionStorage.setItem('loginRememberMe', String(this.f.rememberMe.value));
    this.authenticationService
      .login(
        this.f.email.value as string,
        this.f.password.value as string,
        this.f.rememberMe.value as boolean,
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: boolean) => {
          this.isProcessing.set(false);
          this.loginForm.reset();
          if (this.authenticationService.currentUserValue?.twoFactorSetupRequired) {
            void this.router.navigate(['/2fa-enrollment']);
          } else if (response) {
            void this.router.navigate([this.returnUrl()]);
          } else {
            this.loginError.set({ status: 0, message: 'An error occurred', code: null });
          }
        },
        error: (error: ErrorObject) => {
          this.loginError.set(error);
          this.isProcessing.set(false);
        },
      });
  }
}
